import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import SidebarLayout from "@/components/SidebarLayout";
import DashboardPage from "@/pages/DashboardPage";
import TransactionsPage from "@/pages/TransactionsPage";
import UploadPage from "@/pages/UploadPage";
import AccountsPage from "@/pages/AccountsPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import DocumentsPage from "@/pages/DocumentsPage";
import CustomersPage from "@/pages/CustomersPage";
import InvoicesPage from "@/pages/InvoicesPage";
import QuotesPage from "@/pages/QuotesPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import GovernancePage from "@/pages/GovernancePage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import BankConnectionsPage from "@/pages/BankConnectionsPage";
import TeamPage from "@/pages/TeamPage";
import ExpensesPage from "@/pages/ExpensesPage";
import api from "@/lib/api";

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }
    const token = localStorage.getItem("auth_token");
    const user = localStorage.getItem("user");
    if (token || user) {
      api.get("/auth/me")
        .then(res => {
          localStorage.setItem("user", JSON.stringify(res.data));
          setIsAuthenticated(true);
        })
        .catch(() => {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          setIsAuthenticated(false);
        });
    } else {
      setIsAuthenticated(false);
    }
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <DashboardPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <TransactionsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/upload" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <UploadPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/bank-connections" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <BankConnectionsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/accounts" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <AccountsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <ReportsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <SettingsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <DocumentsPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <CustomersPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <InvoicesPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/quotes" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <QuotesPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/purchase-orders" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <PurchaseOrdersPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <ExpensesPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/governance" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <GovernancePage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <SubscriptionPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <SidebarLayout>
            {(props) => <TeamPage {...props} />}
          </SidebarLayout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
